/// <reference path="../types/vendor-modules.d.ts" />

// ============================================================================
// WORK ITEMS CALENDAR VIEW
// Calendar visualization with drag-to-reschedule
// Date: October 13, 2025
// Updated: February 2026 - ResourceScheduleTimeline moved to KanbanBoard
// ============================================================================

'use client';

import React, { useState, useCallback, useMemo } from 'react';
import { useResponsive } from '@/hooks/useResponsive';
import { getKanbanStylePreset } from '@/components/kanban/KanbanStylePresets';
import WorkItemHoverCard from '@/components/work-items/WorkItemHoverCard';
import { Calendar as BigCalendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { format, parse, startOfWeek, getDay, isSameDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { CalendarDays } from 'lucide-react';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import type { WorkItem } from './UniversalWorkItems';
import type { WorkItemTemplate } from './WorkItemModal';
import { getWorkItemTypeAccent, getWorkItemTypeIdentity } from '@/lib/work-items/typeIdentity';
import { isTerminalWorkItemStatus } from '@/lib/work-items/operationalStatusConfig';
import {
  getWorkItemsAccentPanelStyle,
  WorkItemsTypeAccentBadge,
} from '@/components/work-items/WorkItemsViewAccent';

// Create DnD Calendar component
const Calendar = withDragAndDrop(BigCalendar);

// ============================================================================
// SETUP DATE-FNS LOCALIZER
// ============================================================================

const locales = {
  'en-US': enUS
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// ============================================================================
// TYPES
// ============================================================================

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: WorkItem;
}

interface CalendarDisplayWorkItem extends WorkItem {
  parent_job_number?: string | null;
}

interface CalendarDayWrapperProps {
  children?: React.ReactNode;
  value: Date;
}

interface WorkItemsCalendarViewProps {
  items: WorkItem[];
  onUpdateDueDate: (itemId: string, newDate: string) => void;
  onUpdateSchedule?: (itemId: string, scheduledStart: string, scheduledEnd: string, resourceId: number) => void;
  onViewItem: (item: WorkItem) => void;
  templates?: WorkItemTemplate[];
  /** Resources for timeline view - kept for backward compat but no longer used here */
  resources?: Array<{ id: number; name: string; email?: string }>;
  /** @deprecated Use resources prop instead */
  technicians?: Array<{ id: number; name: string; email: string }>;
  /** Hide the Schedule View button - no longer applicable, kept for backward compat */
  hideScheduleButton?: boolean;
  /** Style preset for consistent theming across views */
  stylePreset?: string;
  loading?: boolean;
  activeItemType?: string | null;
  activeItemTypeLabel?: string;
  primaryColor?: string;
  /** When true, events cannot be drag-rescheduled (lifecycle-managed items
   *  like voting sessions where the window is owned by a bespoke API). */
  readOnly?: boolean;
}

type CalendarView = (typeof Views)[keyof typeof Views];

function CalendarEventContent({ event }: { event: CalendarEvent }) {
  return (
    <WorkItemHoverCard as="div" workItem={event.resource} className="h-full w-full">
      <div className="h-full w-full truncate">{event.title}</div>
    </WorkItemHoverCard>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function WorkItemsCalendarView({
  items,
  onUpdateDueDate,
  onUpdateSchedule,
  onViewItem,
  templates = [],
  hideScheduleButton = false,
  stylePreset,
  loading = false,
  activeItemType,
  activeItemTypeLabel,
  primaryColor = '#3b82f6',
  readOnly = false,
}: WorkItemsCalendarViewProps) {
  void onUpdateSchedule;
  void hideScheduleButton;

  const presetViews = getKanbanStylePreset(stylePreset).views;
  const { isMobile } = useResponsive();
  const [view, setView] = useState<CalendarView>(isMobile ? Views.AGENDA : Views.MONTH);
  const [date, setDate] = useState(new Date());
  const activeTypeIdentity = useMemo(
    () => getWorkItemTypeIdentity(activeItemType, primaryColor),
    [activeItemType, primaryColor]
  );
  const activeTypeAccent = useMemo(
    () => getWorkItemTypeAccent(activeItemType, primaryColor),
    [activeItemType, primaryColor]
  );
  const resolvedActiveTypeLabel = activeItemTypeLabel || activeTypeIdentity.label;
  const hasDesktopToolbarActiveType = Boolean(activeItemType && !isMobile);
  const activeToggleStyle = useMemo(() => ({
    backgroundColor: activeTypeAccent.color,
    boxShadow: `0 10px 24px ${activeTypeAccent.shadow}`,
  }), [activeTypeAccent.color, activeTypeAccent.shadow]);
  
  // Helper to get template for an item
  const getTemplate = useCallback((item: WorkItem): WorkItemTemplate | null => {
    if (!item.item_subtype || templates.length === 0) return null;
    return templates.find(t => t.item_subtype === item.item_subtype) || null;
  }, [templates]);

  // Convert work items to calendar events
  const events: CalendarEvent[] = useMemo(() => {
    // For day/week/month views: use scheduled_start for appointments, due_date for others
    // Filter out parent jobs that have child appointments scheduled (to avoid double entries)
    const parentJobIds = new Set(
      items
        .flatMap((item) => {
          if (item.item_type !== 'appointment') {
            return [];
          }

          const parentJobId = item.custom_fields?.parent_job_id;
          return parentJobId ? [String(parentJobId)] : [];
        })
    );
    return items
      .filter(item => {
        if (!item.due_date && !item.scheduled_start) return false;
        // Skip parent jobs that have appointments — the appointments carry the schedule
        if (item.item_type === 'job' && parentJobIds.has(String(item.id))) return false;
        return true;
      })
      .map(item => {
        const calendarItem = item as CalendarDisplayWorkItem;
        // Appointments use scheduled_start/end for accurate calendar placement
        const useSchedule = item.scheduled_start && (item.item_type === 'appointment' || !item.due_date);
        const start = useSchedule ? new Date(item.scheduled_start!) : new Date(item.due_date!);
        const end = useSchedule && item.scheduled_end ? new Date(item.scheduled_end!) : start;
        const template = getTemplate(item);
        const icon = template?.icon || '';
        // For appointments, show parent job number if available
        const displayNumber = item.item_type === 'appointment' && calendarItem.parent_job_number
          ? calendarItem.parent_job_number
          : item.item_number;
        return {
          id: item.id,
          title: `${icon} ${displayNumber}: ${item.title}`,
          start,
          end,
          resource: item
        };
      });
  }, [items, getTemplate]);

  const visibleEventCount = events.length;

  // Handle event drop (drag to reschedule due_date)
  const handleEventDrop = useCallback(
    ({ event, start }: { event: CalendarEvent; start: Date; end: Date }) => {
      const newDateStr = format(start, 'yyyy-MM-dd');
      onUpdateDueDate(event.id, newDateStr);
    },
    [onUpdateDueDate]
  );

  // Handle event click
  const handleSelectEvent = useCallback(
    (event: CalendarEvent) => {
      onViewItem(event.resource);
    },
    [onViewItem]
  );

  // Custom event style getter
  const eventStyleGetter = useCallback((event: CalendarEvent) => {
    const item = event.resource;
    const template = getTemplate(item);
    let backgroundColor = '#3b82f6'; // Default blue
    
    // Color by template if available
    if (template && template.color) {
      backgroundColor = template.color;
    } else {
      // Fallback: Color by priority
      switch (item.priority) {
        case 'critical':
          backgroundColor = '#dc2626'; // Red
          break;
        case 'urgent':
          backgroundColor = '#ea580c'; // Orange-red
          break;
        case 'high':
          backgroundColor = '#f59e0b'; // Orange
          break;
        case 'medium':
          backgroundColor = '#eab308'; // Yellow
          break;
        case 'low':
          backgroundColor = '#10b981'; // Green
          break;
      }
    }

    // Dim if completed
    if (isTerminalWorkItemStatus(item.status, item.item_type)) {
      backgroundColor = '#9ca3af'; // Gray
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '12px',
        padding: '2px 4px'
      }
    };
  }, [getTemplate]);

  // Custom day cell wrapper
  const CustomDayWrapper = useCallback(
    ({ children, value }: CalendarDayWrapperProps) => {
      const isToday = isSameDay(value, new Date());
      return (
        <div
          className={`rbc-day-bg ${isToday ? 'rbc-today' : ''}`}
          style={{
            backgroundColor: isToday ? activeTypeAccent.tint : undefined
          }}
        >
          {children}
        </div>
      );
    },
    [activeTypeAccent.tint]
  );
  
  return (
    <div
      className="h-full flex flex-col overflow-hidden rounded-xl border bg-white/80 dark:bg-gray-900/30"
      style={{
        borderColor: activeTypeAccent.border,
        boxShadow: `0 12px 30px ${activeTypeAccent.shadow}`,
      }}
    >
      {/* Custom Toolbar - Mobile Responsive */}
      <div
        className={`flex flex-col gap-2 border-b p-2.5 sm:flex-row sm:items-center sm:justify-between sm:px-4 sm:py-3 ${presetViews.headerBg} ${presetViews.dividerBorder}`}
        style={getWorkItemsAccentPanelStyle(activeTypeAccent, 'strong')}
      >
            <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto pb-1 sm:pb-0 scrollbar-thin">
              <button
                onClick={() => setView(Views.MONTH)}
                className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm rounded whitespace-nowrap ${
                  view === Views.MONTH
                    ? 'text-white'
                    : `${presetViews.buttonBg} ${presetViews.buttonText}`
                }`}
                style={view === Views.MONTH ? activeToggleStyle : undefined}
              >
                {isMobile ? 'Mon' : 'Month'}
              </button>
              <button
                onClick={() => setView(Views.WEEK)}
                className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm rounded whitespace-nowrap ${
                  view === Views.WEEK
                    ? 'text-white'
                    : `${presetViews.buttonBg} ${presetViews.buttonText}`
                }`}
                style={view === Views.WEEK ? activeToggleStyle : undefined}
              >
                {isMobile ? 'Wk' : 'Week'}
              </button>
              <button
                onClick={() => setView(Views.DAY)}
                className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm rounded whitespace-nowrap ${
                  view === Views.DAY
                    ? 'text-white'
                    : `${presetViews.buttonBg} ${presetViews.buttonText}`
                }`}
                style={view === Views.DAY ? activeToggleStyle : undefined}
              >
                Day
              </button>
              <button
                onClick={() => setView(Views.AGENDA)}
                className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm rounded whitespace-nowrap ${
                  view === Views.AGENDA
                    ? 'text-white'
                    : `${presetViews.buttonBg} ${presetViews.buttonText}`
                }`}
                style={view === Views.AGENDA ? activeToggleStyle : undefined}
              >
                {isMobile ? 'List' : 'Agenda'}
              </button>
              

            </div>
            <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2">
              {activeItemType && (
                <WorkItemsTypeAccentBadge label={resolvedActiveTypeLabel} accent={activeTypeAccent} />
              )}
              <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-medium ${presetViews.buttonBg} ${presetViews.buttonText}`} style={{ borderColor: activeTypeAccent.border }}>
                <span>{visibleEventCount}</span>
                <span className="opacity-70">scheduled</span>
              </span>
            </div>
          </div>

          {/* Legend - Collapsed on mobile */}
          {!isMobile && (
          <div className={`flex flex-wrap items-center gap-3 px-3 py-2.5 sm:px-4 ${presetViews.headerBg} border-b ${presetViews.dividerBorder}`} style={getWorkItemsAccentPanelStyle(activeTypeAccent, 'soft')}>
            {activeItemType && !hasDesktopToolbarActiveType && (
              <WorkItemsTypeAccentBadge
                label={`Current filter: ${resolvedActiveTypeLabel}`}
                accent={activeTypeAccent}
                className="mr-2"
                uppercase={false}
                labelClassName="text-xs font-semibold"
              />
            )}
            <span className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${presetViews.bodyText}`}>Priority</span>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded" style={{ backgroundColor: '#10b981' }}></span>
              <span className={`text-[11px] ${presetViews.mutedText}`}>Low</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded" style={{ backgroundColor: '#eab308' }}></span>
              <span className={`text-[11px] ${presetViews.mutedText}`}>Medium</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded" style={{ backgroundColor: '#f59e0b' }}></span>
              <span className={`text-[11px] ${presetViews.mutedText}`}>High</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded" style={{ backgroundColor: '#ea580c' }}></span>
              <span className={`text-[11px] ${presetViews.mutedText}`}>Urgent</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded" style={{ backgroundColor: '#dc2626' }}></span>
              <span className={`text-[11px] ${presetViews.mutedText}`}>Critical</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded" style={{ backgroundColor: '#9ca3af' }}></span>
              <span className={`text-[11px] ${presetViews.mutedText}`}>Completed</span>
            </div>
          </div>
          )}

          {/* Calendar */}
          <div className={`flex-1 ${isMobile ? 'p-2' : 'p-4'}`} style={{ minHeight: isMobile ? '400px' : '600px' }}>
            {loading ? (
              <div className="h-full min-h-[400px] animate-pulse rounded-xl border border-gray-100 bg-gray-50/80 p-4 dark:border-gray-800/60 dark:bg-gray-900/40">
                <div className="mb-4 flex items-center gap-2">
                  {Array.from({ length: 7 }).map((_, index) => (
                    <div key={`calendar-loading-day-${index}`} className="h-8 flex-1 rounded-lg bg-gray-200 dark:bg-gray-800/70" />
                  ))}
                </div>
                <div className="grid h-[calc(100%-3rem)] grid-cols-7 gap-2">
                  {Array.from({ length: 35 }).map((_, index) => (
                    <div key={`calendar-loading-cell-${index}`} className="rounded-lg border border-gray-100 bg-white/80 dark:border-gray-800/60 dark:bg-gray-950/40">
                      <div className="p-2">
                        <div className="mb-2 h-3 w-8 rounded bg-gray-200 dark:bg-gray-800/70" />
                        {index % 4 === 0 && <div className="h-5 w-full rounded-md bg-gray-200 dark:bg-gray-800/70" />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : visibleEventCount === 0 ? (
              <div className="flex h-full min-h-[400px] items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white/70 px-6 py-10 text-center dark:border-gray-700 dark:bg-gray-900/20">
                <div className="max-w-sm">
                  <div
                    className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl border"
                    style={{
                      borderColor: activeTypeAccent.border,
                      backgroundColor: activeTypeAccent.tintStrong,
                      color: activeTypeAccent.color,
                    }}
                  >
                    <CalendarDays className="h-8 w-8" />
                  </div>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">No scheduled items to display</p>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Add a due date or scheduled time to visualize work items on the calendar.
                  </p>
                </div>
              </div>
            ) : (
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{ height: '100%' }}
                view={view}
                onView={setView}
                date={date}
                onNavigate={setDate}
                onSelectEvent={handleSelectEvent}
                onEventDrop={handleEventDrop}
                eventPropGetter={eventStyleGetter}
                components={{
                  event: CalendarEventContent,
                  dateCellWrapper: CustomDayWrapper
                }}
                formats={{
                  timeGutterFormat: (date: Date) => format(date, 'HH:mm'),
                  eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
                    `${format(start, 'HH:mm')} – ${format(end, 'HH:mm')}`,
                  agendaTimeFormat: (date: Date) => format(date, 'HH:mm'),
                  agendaTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
                    `${format(start, 'HH:mm')} – ${format(end, 'HH:mm')}`,
                }}
                draggableAccessor={() => !isMobile && !readOnly} // Disable drag on mobile / read-only surfaces
                resizableAccessor={() => false}
                views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
                popup
                selectable
              />
            )}
          </div>

      {/* Custom Styles */}
      <style>{`
        .rbc-calendar {
          font-family: inherit;
        }
        
        .rbc-header {
          padding: 12px 3px;
          font-weight: 600;
          color: #374151;
          background-color: #f9fafb;
          border-bottom: 2px solid #e5e7eb;
        }
        
        .dark .rbc-header {
          color: #d1d5db;
          background-color: #1f2937;
          border-bottom-color: #374151;
        }
        
        .rbc-today {
          background-color: ${activeTypeAccent.tint} !important;
        }
        
        .dark .rbc-today {
          background-color: ${activeTypeAccent.tintStrong} !important;
        }
        
        .rbc-event {
          padding: 2px 5px;
          cursor: pointer;
          transition: opacity 0.2s;
        }
        
        .rbc-event:hover {
          opacity: 1 !important;
        }
        
        .rbc-event-label {
          font-size: 11px;
        }
        
        .rbc-toolbar {
          padding: 15px;
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          align-items: center;
          background-color: #f9fafb;
          border-radius: 8px;
          margin-bottom: 15px;
        }
        
        .dark .rbc-toolbar {
          background-color: #1f2937;
        }
        
        .rbc-toolbar button {
          color: #374151;
          border: 1px solid #d1d5db;
          background-color: white;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .dark .rbc-toolbar button {
          color: #d1d5db;
          border-color: #4b5563;
          background-color: #374151;
        }
        
        .rbc-toolbar button:hover {
          background-color: #f3f4f6;
          border-color: #9ca3af;
        }
        
        .dark .rbc-toolbar button:hover {
          background-color: #4b5563;
          border-color: #6b7280;
        }
        
        .rbc-toolbar button.rbc-active {
          background-color: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }
        
        .rbc-toolbar button.rbc-active:hover {
          background-color: #2563eb;
        }
        
        .rbc-month-view,
        .rbc-time-view,
        .rbc-agenda-view {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
        }
        
        .dark .rbc-month-view,
        .dark .rbc-time-view,
        .dark .rbc-agenda-view {
          border-color: #374151;
          background-color: #1f2937;
        }
        
        .rbc-day-slot .rbc-time-slot {
          border-top: 1px solid #f3f4f6;
        }
        
        .dark .rbc-day-slot .rbc-time-slot {
          border-top-color: #374151;
        }
        
        .rbc-current-time-indicator {
          background-color: ${activeTypeAccent.color};
          height: 2px;
        }
        
        .rbc-agenda-view table {
          border: none;
        }
        
        .rbc-agenda-view table tbody > tr > td {
          padding: 12px;
          border-bottom: 1px solid #f3f4f6;
        }
        
        .dark .rbc-agenda-view table tbody > tr > td {
          border-bottom-color: #374151;
        }
        
        .rbc-agenda-date-cell,
        .rbc-agenda-time-cell {
          white-space: nowrap;
          color: #6b7280;
        }
        
        .dark .rbc-agenda-date-cell,
        .dark .rbc-agenda-time-cell {
          color: #9ca3af;
        }
        
        .rbc-agenda-event-cell {
          width: 100%;
        }
        
        /* Month view improvements */
        .rbc-month-view {
          height: calc(100vh - 300px); /* Fixed height for month view */
          min-height: 600px;
        }
        
        .rbc-month-row {
          border-top: 1px solid #e5e7eb;
          min-height: 80px;
          flex: 1 0 0%; /* Equal height rows */
        }
        
        .dark .rbc-month-row {
          border-top-color: #374151;
        }
        
        .rbc-row-content {
          height: 100%;
        }
        
        .rbc-date-cell {
          padding: 8px;
          text-align: right;
        }
        
        .dark .rbc-date-cell {
          color: #d1d5db;
        }
        
        .rbc-off-range {
          color: #9ca3af;
        }
        
        .dark .rbc-off-range {
          color: #6b7280;
        }
        
        .rbc-off-range-bg {
          background-color: #fafafa;
        }
        
        .dark .rbc-off-range-bg {
          background-color: #111827;
        }
        
        /* Week/Day view improvements */
        .rbc-time-header-content {
          border-left: 1px solid #e5e7eb;
        }
        
        .dark .rbc-time-header-content {
          border-left-color: #374151;
        }
        
        .rbc-time-content {
          border-top: 1px solid #e5e7eb;
        }
        
        .dark .rbc-time-content {
          border-top-color: #374151;
        }
        
        .rbc-time-slot {
          min-height: 40px;
        }
        
        .rbc-timeslot-group {
          border-bottom: 1px solid #f3f4f6;
          min-height: 80px;
        }
        
        .dark .rbc-timeslot-group {
          border-bottom-color: #374151;
        }
        
        /* Overlay/Popup */
        .rbc-overlay {
          background-color: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          padding: 10px;
        }
        
        .dark .rbc-overlay {
          background-color: #1f2937;
          border-color: #374151;
        }
        
        .rbc-overlay-header {
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 8px;
          margin-bottom: 8px;
          font-weight: 600;
          color: #374151;
        }
        
        .dark .rbc-overlay-header {
          border-bottom-color: #374151;
          color: #d1d5db;
        }
      `}</style>
    </div>
  );
}
